import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { fetchGooglePlacePhotos, type GooglePlacePhotoChoice } from "../components/PlacePicker";
import type { TripSummary } from "./tripStore";
import { getOfflineTripVisualBlob } from "./tripOffline";

export function TripPhotoVisual({
  dataUrl,
  placeId,
  photoIndex = 0,
  className,
  fallback,
  fallbackSrc
}: {
  dataUrl?: string;
  placeId?: string;
  photoIndex?: number;
  className: string;
  fallback?: string;
  fallbackSrc?: string;
}) {
  const [photo, setPhoto] = useState<GooglePlacePhotoChoice | null>(null);
  const [failedSrc, setFailedSrc] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPhoto(null);
    if (!placeId || dataUrl) return;
    void fetchGooglePlacePhotos(placeId, Math.max(1, photoIndex + 1)).then((photos) => {
      if (!cancelled) setPhoto(photos[Math.min(photoIndex, Math.max(0, photos.length - 1))] || null);
    }).catch(() => {
      if (!cancelled) setPhoto(null);
    });
    return () => { cancelled = true; };
  }, [dataUrl, placeId, photoIndex]);

  const requestedSrc = dataUrl || photo?.url;
  const src = requestedSrc && requestedSrc !== failedSrc ? requestedSrc : fallbackSrc;
  if (src) return <img className={className} src={src} alt="" onError={src === fallbackSrc ? undefined : () => setFailedSrc(src)} />;
  return fallback ? <span className={`${className} fallback`} aria-hidden="true">{fallback}</span> : null;
}

export function TripCoverVisual({
  trip,
  className,
  fallback = "✈"
}: {
  trip: Pick<TripSummary,
    "id" |
    "coverPhotoDataUrl" | "coverPhotoGooglePlaceId" | "coverPhotoIndex" |
    "profilePhotoDataUrl" | "profilePhotoGooglePlaceId" | "profilePhotoIndex"
  >;
  className: string;
  fallback?: string;
}) {
  const { user } = useAuth();
  const [offlineDataUrl, setOfflineDataUrl] = useState("");
  const hasCover = Boolean(trip.coverPhotoDataUrl || trip.coverPhotoGooglePlaceId);
  const visualKind = hasCover ? "cover" : "profile";

  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setOfflineDataUrl("");
    if (!user?.uid || !trip.id) return;
    void getOfflineTripVisualBlob(user.uid, trip.id, visualKind).then((blob) => {
      if (!active) return;
      if (!blob) {
        setOfflineDataUrl("");
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setOfflineDataUrl(objectUrl);
    }).catch(() => undefined);
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user?.uid, trip.id, visualKind]);

  return <TripPhotoVisual
    dataUrl={offlineDataUrl || (hasCover ? trip.coverPhotoDataUrl : trip.profilePhotoDataUrl)}
    placeId={hasCover ? trip.coverPhotoGooglePlaceId : trip.profilePhotoGooglePlaceId}
    photoIndex={hasCover ? trip.coverPhotoIndex : trip.profilePhotoIndex}
    className={className}
    fallback={fallback}
    fallbackSrc={`${import.meta.env.BASE_URL}images/travel-window-default.webp`}
  />;
}
